import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import GroupsIcon from '@mui/icons-material/Groups';

import AcademicDashboard from './components/AcademicProgress/AcademicDashboard';
import EnrollmentTrends from './components/Enrollment/EnrollmentTrends';
import SchoolSupervision from './components/Supervision/SchoolSupervision';
import TeacherEvaluation from './components/Supervision/TeacherEvaluation';
import DepartmentSupervision from './components/Supervision/DepartmentSupervision';

const theme = createTheme({
  palette: {
    primary: {
      main: '#87CEEB', // Light blue
      light: '#B0E0E6',
      dark: '#4682B4',
    },
    secondary: {
      main: '#FFEBCD', // Cream color
      light: '#FFF8DC',
      dark: '#DEB887',
    },
    background: {
      default: '#F5F5F5',
      paper: '#FFFFFF',
    },
  },
});

const drawerWidth = 240;

function App() {
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const menuItems = [
    { text: 'Academic Progress', icon: <DashboardIcon />, path: '/' },
    { text: 'Enrollment Trends', icon: <PeopleIcon />, path: '/enrollment' },
    { text: 'School Overview', icon: <AssessmentIcon />, path: '/supervision' },
    { text: 'Teacher Evaluation', icon: <SupervisorAccountIcon />, path: '/teacher-evaluation' },
    { text: 'Department Supervision', icon: <GroupsIcon />, path: '/department-supervision' },
  ];

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          Innermans School
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem button key={item.text} component="a" href={item.path}>
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Box sx={{ display: 'flex' }}>
          <AppBar
            position="fixed"
            sx={{ width: { sm: `calc(100% - ${drawerWidth}px)` }, ml: { sm: `${drawerWidth}px` } }}
          >
            <Toolbar>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2, display: { sm: 'none' } }}
              >
                <MenuIcon />
              </IconButton>
              <Typography variant="h6" noWrap component="div">
                School Management System
              </Typography>
            </Toolbar>
          </AppBar>
          <Box
            component="nav"
            sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
          >
            <Drawer
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              ModalProps={{
                keepMounted: true, // Better open performance on mobile.
              }}
              sx={{
                display: { xs: 'block', sm: 'none' },
                '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
              }}
            >
              {drawer}
            </Drawer>
            <Drawer
              variant="permanent"
              sx={{
                display: { xs: 'none', sm: 'block' },
                '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
              }}
              open
            >
              {drawer}
            </Drawer>
          </Box>
          <Box
            component="main"
            sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` } }}
          >
            <Toolbar />
            <Routes>
              <Route path="/" element={<AcademicDashboard />} />
              <Route path="/enrollment" element={<EnrollmentTrends />} />
              <Route path="/supervision" element={<SchoolSupervision />} />
              <Route path="/teacher-evaluation" element={<TeacherEvaluation />} />
              <Route path="/department-supervision" element={<DepartmentSupervision />} />
            </Routes>
          </Box>
        </Box>
      </Router>
    </ThemeProvider>
  );
}

export default App;
